import { useId } from 'react'
import { has, text, type ContentMap } from '../api/content'

/**
 * Where the studio's Facebook and Instagram live on the public site.
 *
 * **One component, two placements** (D-129) — the header and the footer. They
 * render the same links at different weights, so a URL change or a new network
 * lands in both rather than in whichever one somebody remembered.
 *
 * The marks are drawn rather than imported. `lucide-react` removed its brand
 * glyphs, and these are its own ISC-licensed geometry — which also means they
 * sit correctly beside the other icons on the page instead of being the one
 * filled shape among strokes.
 */

/*
 * Brand colours, always on (D-129 amended).
 *
 * The problem Tonya reported was that these were *not seen*, and a colour that
 * only appears on hover cannot fix that — you get it once you are already
 * pointing at the thing you failed to find. Facebook blue and the Instagram
 * gradient are recognised before they are read, which is the whole point.
 *
 * It costs some palette purity: the studio's accent is a pink, and a saturated
 * blue beside it is a third colour in the header. Worth it here and nowhere
 * else — these are the two most recognisable marks on the internet, and being
 * recognisable is their entire job.
 */
const FACEBOOK_BLUE = '#1877F2'

function FacebookMark({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={FACEBOOK_BLUE}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  )
}

function InstagramMark({ size = 16 }: { size?: number }) {
  /*
   * Instagram has no single colour — the gradient *is* the mark, so a flat pink
   * reads as an approximation of it.
   *
   * `useId` because this renders three times on one page (header, menu,
   * footer). A hardcoded gradient id would put duplicate ids in the document:
   * browsers cope by using the first, but it is invalid, and the kind of thing
   * that breaks the day somebody renders one inside a shadow root.
   */
  const gradientId = useId()

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={`url(#${gradientId})`}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <defs>
        {/* Corner to corner, matching the direction of the official mark. */}
        <linearGradient id={gradientId} x1="0" y1="24" x2="24" y2="0">
          <stop offset="0%" stopColor="#F58529" />
          <stop offset="35%" stopColor="#DD2A7B" />
          <stop offset="70%" stopColor="#8134AF" />
          <stop offset="100%" stopColor="#515BD4" />
        </linearGradient>
      </defs>

      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  )
}

type Network = { key: string; label: string; icon: (props: { size?: number }) => React.ReactElement }

const NETWORKS: Network[] = [
  { key: 'social.facebook', label: 'Facebook', icon: FacebookMark },
  { key: 'social.instagram', label: 'Instagram', icon: InstagramMark },
]

/**
 * Compact icons for the header.
 *
 * **Desktop only.** The mobile header already carries a logo, a menu button and
 * the theme toggle; two more would crowd the one row a phone has. On a phone
 * they appear in the opened menu instead, where there is room to name them.
 */
export function SocialHeaderLinks({ content }: { content: ContentMap | undefined }) {
  const shown = NETWORKS.filter((n) => has(content, n.key))
  if (shown.length === 0) return null

  return (
    <div className="hidden items-center gap-1 sm:flex">
      {shown.map((network) => (
        <a
          key={network.key}
          href={text(content, network.key)}
          target="_blank"
          rel="noreferrer noopener"
          // Named for a screen reader even though the label is hidden — an
          // unlabelled icon link is a dead end for anyone not using their eyes.
          aria-label={`Cabot Dance Academy on ${network.label}`}
          title={network.label}
          // The mark carries its own colour now, so hover is a background tint
          // and a slight lift rather than a colour change — there is nothing
          // left to recolour, and a brand mark that shifts hue on hover looks
          // broken rather than interactive.
          className="grid size-8 place-items-center rounded-lg transition-all hover:bg-surface-2 hover:scale-110"
        >
          <network.icon size={17} />
        </a>
      ))}
    </div>
  )
}

/**
 * The footer pair, given their own row.
 *
 * Previously two grey words at the faint end of the ink scale, sharing a line
 * with the phone number — which is how they got lost (D-129). Bordered,
 * labelled and in the accent colour, they read as somewhere to go rather than
 * as small print.
 */
export function SocialFooterLinks({ content }: { content: ContentMap | undefined }) {
  const shown = NETWORKS.filter((n) => has(content, n.key))
  if (shown.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {shown.map((network) => (
        <a
          key={network.key}
          href={text(content, network.key)}
          target="_blank"
          rel="noreferrer noopener"
          // The label stays in the studio's ink; only the mark is branded. Two
          // saturated colours side by side in one small button would compete,
          // and the icon is the part that has to be recognised.
          className="flex items-center gap-2 rounded-lg border border-line-2 bg-surface px-3.5 py-2 text-[13px] font-semibold text-ink-2 no-underline transition-colors hover:border-ink-3 hover:text-ink"
        >
          <network.icon size={16} />
          {network.label}
        </a>
      ))}
    </div>
  )
}

/** For the mobile menu, where the header icons are hidden. */
export function SocialMenuLinks({
  content,
  onNavigate,
}: {
  content: ContentMap | undefined
  onNavigate: () => void
}) {
  const shown = NETWORKS.filter((n) => has(content, n.key))
  if (shown.length === 0) return null

  return (
    <>
      {shown.map((network) => (
        <a
          key={network.key}
          href={text(content, network.key)}
          target="_blank"
          rel="noreferrer noopener"
          onClick={onNavigate}
          className="flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13.5px] font-semibold text-ink-2 no-underline hover:text-ink"
        >
          <network.icon size={15} />
          {network.label}
        </a>
      ))}
    </>
  )
}
